import type { Baseline, Capacity, DayOfWeek, Forecast } from '@repo/contracts';

import type { DayCapacity } from '@/domain/templates/staffing';
import type { DemandScaling } from '@/types/template';

/** A transient what-if forecast for the preview, mirroring {@link DemandScaling}'s shape. */
export interface DemandForecast {
  weekly?: number;
  daily?: number[];
}

/** Parses a demand input; blank/negative/non-finite collapses to 0. Shared by the editors. */
export function toDemandValue(raw: string): number {
  const value = Number(raw);
  return Number.isFinite(value) && value > 0 ? value : 0;
}

/** Returns a 7-day series with `index` replaced by `value`. */
export function withDayValue(daily: number[], index: number, value: number): number[] {
  const next = [...daily];
  next[index] = value;
  return next;
}

/** Sensible starting values when a user first turns scaling on (mirrors the design prototype). */
export function defaultDemandScaling(): DemandScaling {
  return {
    enabled: false,
    metric: 'guests',
    period: 'weekly',
    weekly: 700,
    daily: [120, 90, 90, 110, 140, 200, 150],
  };
}

/**
 * The per-day baseline value for the chosen metric: a `weekly` total spread
 * evenly across seven days, or the specific `daily` figure (Mon–Sun).
 */
export function baselineValueForDay(scaling: DemandScaling, dayOfWeek: DayOfWeek): number {
  return scaling.period === 'weekly' ? scaling.weekly / 7 : (scaling.daily[dayOfWeek - 1] ?? 0);
}

/**
 * The per-day forecast value, falling back to the baseline for any day the
 * what-if forecast leaves unset (so the preview sits at base counts by default).
 */
export function forecastValueForDay(
  scaling: DemandScaling,
  forecast: DemandForecast,
  dayOfWeek: DayOfWeek,
): number {
  if (scaling.period === 'weekly') {
    return (forecast.weekly ?? scaling.weekly) / 7;
  }
  return forecast.daily?.[dayOfWeek - 1] ?? scaling.daily[dayOfWeek - 1] ?? 0;
}

/**
 * Builds the {@link DayCapacity} a day's shift counts scale against: the single
 * metric's baseline (omitted when ≤ 0 so the scaler falls back to the base count
 * rather than dividing by zero) and forecast value.
 */
export function dayCapacity(
  scaling: DemandScaling,
  forecast: DemandForecast,
  dayOfWeek: DayOfWeek,
): DayCapacity {
  const baselineValue = baselineValueForDay(scaling, dayOfWeek);
  const baseline: Capacity = {};
  if (baselineValue > 0) baseline[scaling.metric] = baselineValue;
  const forecastCapacity: Capacity = {};
  forecastCapacity[scaling.metric] = forecastValueForDay(scaling, forecast, dayOfWeek);
  return { baseline, forecast: forecastCapacity };
}

/**
 * Builds the rotagen `Forecast` (per-day {@link Capacity} keyed by ISO date) for
 * a generation period from the template's scaling settings and a what-if
 * forecast. Each day takes the single metric's {@link forecastValueForDay} —
 * the what-if value where set, otherwise the baseline — so the proposal's
 * headcount matches the builder's live preview for the same inputs.
 *
 * `days` is the period's `{ iso, isoWeekday }` list (see `enumerateWeekDays`).
 * Returns undefined when scaling is off or there are no days, so callers omit
 * `forecast` entirely (shifts then sit at their base count).
 */
export function forecastForWeek(
  scaling: DemandScaling | undefined,
  forecast: DemandForecast,
  days: { iso: string; isoWeekday: number }[],
): Forecast | undefined {
  if (!scaling?.enabled || days.length === 0) return undefined;

  const byDate: Record<string, Capacity> = {};
  for (const { iso, isoWeekday } of days) {
    byDate[iso] = {
      [scaling.metric]: forecastValueForDay(scaling, forecast, isoWeekday as DayOfWeek),
    };
  }
  return { days: byDate };
}

/**
 * Derives the contract `RotaTemplate.baseline` (per-day {@link Capacity}) from the
 * UI scaling settings, for rotagen: a `weekly` total becomes a `default` of
 * total ÷ 7; `daily` figures become `forDayOfWeek` entries (ISO 1 = Mon). Returns
 * undefined when scaling is off or no positive figure is set.
 */
export function baselineFromDemandScaling(
  scaling: DemandScaling | undefined,
): Baseline | undefined {
  if (!scaling?.enabled) return undefined;

  if (scaling.period === 'weekly') {
    const perDay = scaling.weekly / 7;
    if (perDay <= 0) return undefined;
    return { default: { [scaling.metric]: perDay } };
  }

  const forDayOfWeek: Partial<Record<DayOfWeek, Capacity>> = {};
  scaling.daily.forEach((value, index) => {
    if (value > 0) forDayOfWeek[(index + 1) as DayOfWeek] = { [scaling.metric]: value };
  });
  return Object.keys(forDayOfWeek).length > 0 ? { forDayOfWeek } : undefined;
}
